<template>
    <div class="page-container-v2">
        <page-header
            title="GTIN 관리"
            sub-title="GTIN 코드 데이터를 관리합니다"
        />

        <!-- 필터 영역 -->
        <div class="filter-section">
            <a-row :gutter="16" align="middle">
                <a-col :xs="24" :sm="12" :md="6">
                    <a-input
                        v-model:value="filters.brandName"
                        placeholder="브랜드명"
                        allow-clear
                        @keyup.enter="handleSearch"
                    />
                </a-col>
                <a-col :xs="24" :sm="12" :md="6">
                    <a-input
                        v-model:value="filters.modelName"
                        placeholder="모델명"
                        allow-clear
                        @keyup.enter="handleSearch"
                    />
                </a-col>
                <a-col :xs="24" :sm="12" :md="6">
                    <a-input
                        v-model:value="filters.productLine"
                        placeholder="제품선택"
                        allow-clear
                        @keyup.enter="handleSearch"
                    />
                </a-col>
                <a-col :xs="24" :sm="12" :md="6">
                    <a-input
                        v-model:value="filters.gtinCode"
                        placeholder="GTIN 코드"
                        allow-clear
                        @keyup.enter="handleSearch"
                    />
                </a-col>
                <a-col :xs="24" class="filter-buttons">
                    <a-space>
                        <a-button type="primary" @click="handleSearch">
                            <i class="fa-solid fa-magnifying-glass" style="margin-right: 6px;" />
                            검색
                        </a-button>
                        <a-button @click="handleReset">
                            <i class="fa-solid fa-rotate-right" style="margin-right: 6px;" />
                            초기화
                        </a-button>
                    </a-space>
                </a-col>
            </a-row>
        </div>

        <!-- 탭 및 컨트롤 영역 -->
        <div class="control-section">
            <a-tabs v-model:activeKey="activeTab" @change="handleTabChange">
                <a-tab-pane key="kr" tab="국내">
                    <template #tab>
                        <span><i class="fa-solid fa-flag" style="margin-right: 6px;" />국내</span>
                    </template>
                </a-tab-pane>
                <a-tab-pane key="cn" tab="중국">
                    <template #tab>
                        <span><i class="fa-solid fa-globe" style="margin-right: 6px;" />중국</span>
                    </template>
                </a-tab-pane>
            </a-tabs>
            <div class="control-buttons">
                <a-upload
                    :before-upload="handleBeforeUpload"
                    :show-upload-list="false"
                    accept=".xlsx,.xls"
                >
                    <a-button :loading="uploading">
                        <i class="fa-solid fa-file-import" style="margin-right: 6px;" />
                        엑셀 업로드
                    </a-button>
                </a-upload>
            </div>
        </div>

        <!-- 테이블 영역 -->
        <div class="table-with-search">
            <div class="table-info-filter">
                <div class="total-div">
                    <p>
                        총 <span class="number-highlight">{{ formatCommaNumber(totalCount) }}</span>건
                    </p>
                </div>
                <div class="search-container">
                    <a-select
                        v-model:value="rowsPerPage"
                        @change="handleRowsPerPageChange"
                        style="width: 150px;"
                    >
                        <a-select-option :value="10">10개씩 보기</a-select-option>
                        <a-select-option :value="20">20개씩 보기</a-select-option>
                        <a-select-option :value="50">50개씩 보기</a-select-option>
                    </a-select>
                </div>
            </div>

            <a-table
                class="ant-table-striped"
                size="small"
                :columns="currentColumns"
                :data-source="dataSource"
                :pagination="false"
                :loading="loading"
                :scroll="{ x: 1000 }"
                row-key="_id"
                v-if="dataSource.length > 0 || loading"
            >
                <template #bodyCell="{ column, record, index }">
                    <template v-if="column.key === 'index'">
                        {{ totalCount - (paginationData.currentPage - 1) * rowsPerPage - index }}
                    </template>
                    <template v-else-if="column.key === 'gtinCode'">
                        <a-typography-text copyable>{{ record.gtinCode }}</a-typography-text>
                    </template>
                    <template v-else-if="column.key === 'qrCode'">
                        <a-button type="link" size="small" @click="showQrModal(record.gtinCode)">
                            <i class="fa-solid fa-qrcode" />
                        </a-button>
                    </template>
                    <template v-else-if="column.key === 'productLine'">
                        {{ record.productLine || '-' }}
                    </template>
                    <template v-else-if="column.key === 'status'">
                        <a-tag :color="getStatusColor(record.status)">
                            {{ getStatusText(record.status) }}
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'createdAt'">
                        {{ formatDate(record.createdAt) }}
                    </template>
                    <template v-else-if="column.key === 'updatedAt'">
                        {{ formatDate(record.updatedAt) }}
                    </template>
                </template>
            </a-table>

            <div class="no-result-container" v-if="!loading && dataSource.length === 0">
                <i class="fa-solid fa-exclamation-triangle" />
                <span>검색 결과가 없습니다</span>
            </div>
        </div>

        <!-- 페이지네이션 -->
        <pagination
            v-if="!loading && dataSource.length > 0"
            :page-info="paginationData"
            page-name="GtinManagementList"
            @change-page="handlePageChange"
        />

        <!-- QR 코드 모달 -->
        <a-modal
            v-model:open="qrModalVisible"
            title="GTIN QR 코드"
            :footer="null"
            :width="360"
            centered
        >
            <div class="qr-modal-content">
                <div class="qr-code-wrapper">
                    <img
                        v-if="qrModalData.qrDataUrl"
                        :src="qrModalData.qrDataUrl"
                        alt="QR Code"
                        class="qr-code-image"
                    />
                    <a-spin v-else />
                </div>
                <div class="gtin-code">
                    <a-typography-text copyable :content="qrModalData.gtinCode">
                        {{ qrModalData.gtinCode }}
                    </a-typography-text>
                </div>
                <p class="qr-guide">{{ activeTab === 'cn' ? '미니앱으로' : '스마트폰으로' }} QR 코드를 스캔하세요</p>
            </div>
        </a-modal>
    </div>
</template>

<script>
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import QRCode from 'qrcode';
import { formatCommaNumber } from '@/utils/utils';
import Pagination from '@/views/components/Pagination.vue';
import PageHeader from '@/views/components/PageHeader.vue';
import gtinApi from './api';

export default {
    name: 'GtinManagementList',
    components: {
        Pagination,
        PageHeader,
    },
    data() {
        return {
            formatCommaNumber,
            activeTab: 'kr',
            loading: false,
            uploading: false,
            api: null,

            // 필터 상태
            filters: {
                brandName: '',
                modelName: '',
                productLine: '',
                gtinCode: '',
            },

            // 테이블 데이터
            dataSource: [],
            totalCount: 0,

            // 페이지네이션
            rowsPerPage: 10,
            paginationData: {
                currentPage: 1,
                totalPages: 0,
            },

            // 국내 테이블 컬럼
            krColumns: [
                { title: 'No', key: 'index', width: 60, align: 'center', fixed: 'left' },
                { title: '브랜드명', dataIndex: 'brandName', key: 'brandName', width: 150 },
                { title: '모델명', dataIndex: 'modelName', key: 'modelName', width: 200 },
                { title: 'GTIN 코드', dataIndex: 'gtinCode', key: 'gtinCode', width: 180 },
                { title: 'QR', key: 'qrCode', width: 70, align: 'center' },
                { title: '제품선택', dataIndex: 'productLine', key: 'productLine', width: 140 },
                { title: '상태', dataIndex: 'status', key: 'status', width: 100, align: 'center' },
                { title: '등록일', dataIndex: 'createdAt', key: 'createdAt', width: 120 },
                { title: '수정일', dataIndex: 'updatedAt', key: 'updatedAt', width: 120 },
            ],

            // 중국 테이블 컬럼
            cnColumns: [
                { title: 'No', key: 'index', width: 60, align: 'center', fixed: 'left' },
                { title: '상품코드', dataIndex: 'productCode', key: 'productCode', width: 160 },
                { title: 'GTIN 코드', dataIndex: 'gtinCode', key: 'gtinCode', width: 160 },
                { title: 'QR', key: 'qrCode', width: 70, align: 'center' },
                { title: '제품선택', dataIndex: 'productLine', key: 'productLine', width: 140 },
                { title: '상태', dataIndex: 'status', key: 'status', width: 100, align: 'center' },
                { title: '등록일', dataIndex: 'createdAt', key: 'createdAt', width: 120 },
                { title: '수정일', dataIndex: 'updatedAt', key: 'updatedAt', width: 120 },
            ],

            // QR 모달 상태
            qrModalVisible: false,
            qrModalData: {
                gtinCode: '',
                qrDataUrl: '',
            },
        };
    },
    computed: {
        currentColumns() {
            return this.activeTab === 'kr' ? this.krColumns : this.cnColumns;
        },
    },
    created() {
        // API 인스턴스 초기화
        this.api = gtinApi(this.$apiInstance, this.$store);
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        // 데이터 조회
        async fetchData() {
            this.loading = true;

            const params = {
                page: this.paginationData.currentPage,
                limit: this.rowsPerPage,
            };

            // 필터 조건 추가
            if (this.filters.brandName) {
                params.brandName = this.filters.brandName;
            }
            if (this.filters.modelName) {
                params.modelName = this.filters.modelName;
            }
            if (this.filters.productLine) {
                params.productLine = this.filters.productLine;
            }
            if (this.filters.gtinCode) {
                params.gtinCode = this.filters.gtinCode;
            }

            const apiMethod = this.activeTab === 'kr'
                ? this.api.getKrProducts
                : this.api.getCnProducts;

            await apiMethod(
                params,
                (response) => {
                    if (response.data) {
                        this.dataSource = response.data.data || [];
                        this.totalCount = response.data.total || 0;
                        this.paginationData.totalPages = response.data.pages || 0;
                    }
                    this.loading = false;
                },
                (error) => {
                    console.error('데이터 조회 실패:', error);
                    message.error('데이터 조회에 실패했습니다');
                    this.loading = false;
                }
            );
        },

        // 검색
        handleSearch() {
            this.paginationData.currentPage = 1;
            this.fetchData();
        },

        // 초기화
        handleReset() {
            this.filters = {
                brandName: '',
                modelName: '',
                productLine: '',
                gtinCode: '',
            };
            this.paginationData.currentPage = 1;
            this.fetchData();
        },

        // 탭 변경
        handleTabChange(key) {
            this.activeTab = key;
            this.handleReset();
        },

        // 페이지 변경
        handlePageChange(pageInfo) {
            this.paginationData.currentPage = pageInfo.page;
            this.fetchData();
        },

        // 페이지 크기 변경
        handleRowsPerPageChange() {
            this.paginationData.currentPage = 1;
            this.fetchData();
        },

        // 엑셀 업로드 전 처리
        async handleBeforeUpload(file) {
            const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                || file.type === 'application/vnd.ms-excel'
                || file.name.endsWith('.xlsx')
                || file.name.endsWith('.xls');

            if (!isExcel) {
                message.error('지원하지 않는 파일 형식입니다. xlsx 또는 xls 파일만 업로드 가능합니다.');
                return false;
            }

            this.uploading = true;

            const apiMethod = this.activeTab === 'kr'
                ? this.api.importKrProducts
                : this.api.importCnProducts;

            await apiMethod(
                file,
                (response) => {
                    const result = response.data;
                    message.success(`업로드 완료: 성공 ${result.successCount || 0}건, 실패 ${result.failCount || 0}건`);
                    this.uploading = false;
                    this.fetchData();
                },
                (error) => {
                    console.error('엑셀 업로드 실패:', error);
                    message.error('엑셀 업로드에 실패했습니다');
                    this.uploading = false;
                }
            );

            return false; // 기본 업로드 동작 방지
        },

        // 날짜 포맷
        formatDate(date) {
            if (!date) return '-';
            return dayjs(date).format('YYYY-MM-DD');
        },

        // 상태 색상
        getStatusColor(status) {
            const colorMap = {
                active: 'green',
                inactive: 'default',
                discontinued: 'red',
            };
            return colorMap[status] || 'default';
        },

        // 상태 텍스트
        getStatusText(status) {
            const textMap = {
                active: '활성',
                inactive: '비활성',
                discontinued: '단종',
            };
            return textMap[status] || status || '-';
        },

        // QR 모달 표시
        async showQrModal(gtinCode) {
            this.qrModalData.gtinCode = gtinCode;
            this.qrModalData.qrDataUrl = '';
            this.qrModalVisible = true;

            try {
                const qrDataUrl = await QRCode.toDataURL(gtinCode, {
                    width: 280,
                    margin: 2,
                    color: {
                        dark: '#000000',
                        light: '#FFFFFF',
                    },
                    errorCorrectionLevel: 'M',
                });
                this.qrModalData.qrDataUrl = qrDataUrl;
            } catch (error) {
                console.error('QR 코드 생성 실패:', error);
                message.error('QR 코드 생성에 실패했습니다');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.filter-section {
    background: #fff;
    padding: 16px 20px;
    border-radius: 8px;
    margin-bottom: 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

    .ant-input {
        height: 36px;
    }

    .ant-btn {
        height: 36px;
    }

    .filter-buttons {
        display: flex;
        justify-content: flex-end;
        margin-top: 12px;
    }
}

.control-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background: #fff;
    padding: 0 20px;
    border-radius: 8px;
    margin-bottom: 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

    .ant-tabs {
        margin-bottom: 0;

        :deep(.ant-tabs-nav) {
            margin-bottom: 0;
        }
    }

    .control-buttons {
        display: flex;
        gap: 8px;
        padding: 12px 0;
    }
}

.table-info-filter {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .total-div {
        p {
            margin: 0;
            font-size: 14px;
            color: #333;

            .number-highlight {
                font-weight: 600;
                color: #1890ff;
            }
        }
    }
}

.no-result-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #999;

    i {
        font-size: 48px;
        margin-bottom: 16px;
        color: #ccc;
    }

    span {
        font-size: 14px;
    }
}

.qr-modal-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;

    .qr-code-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 280px;
        height: 280px;
        background: #fff;
        border: 1px solid #f0f0f0;
        border-radius: 8px;
        margin-bottom: 20px;

        .qr-code-image {
            width: 280px;
            height: 280px;
        }
    }

    .gtin-code {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin-bottom: 12px;
        letter-spacing: 1px;
    }

    .qr-guide {
        margin: 0;
        font-size: 13px;
        color: #999;
    }
}

@media screen and (max-width: 767px) {
    .filter-section {
        .ant-row {
            gap: 8px;
        }

        .ant-col {
            margin-bottom: 8px;
        }
    }

    .control-section {
        flex-direction: column;

        .control-buttons {
            width: 100%;
            justify-content: flex-end;
            padding-bottom: 12px;
        }
    }

    .table-info-filter {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
}
</style>
